import { setReportDraftPrincipal } from "../utils/reportDraftStore.js";
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import * as Sentry from "@sentry/react";
import {
  setRefreshHandler,
  setAppSessionActive,
  setSessionPrincipal,
  setLogoutHandler,
  AUTH_OPERATION_STALE,
} from "../utils/apiClient.js";
// P0 fail-closed(docs/p0-auth-fail-closed-spec.md): 복원·login·reauth·expired refresh 는 전부 bounded /api/auth-verify
//  검증 helper 경유(§3~§5). §6 비동기 인증 작업 세대 가드는 createAuthOpGuard.
import {
  verifyCredentialWithServer,
  normalizeEmail,
  isSamePrincipal,
  createAuthOpGuard,
  AUTH_FAIL,
  readAppSession,
} from "../utils/authVerifyClient.js";
// frontend-stability Track B → Session tranche A+B: GIS credential 브로커(앱 전체에서 initialize·콜백 단일 소유).
//  재발급은 브로커 single-flight — 동시 요청이 prompt 를 두 번 띄우지 않는다. logout 은 진행 중 요청을 취소한다.
import {
  requestFreshCredential,
  cancelPendingCredentialRequests,
} from "../utils/gisSilentRefresh.js";
// Session tranche A(docs/session-navigation-ux-spec.md §A): 선제 갱신 스케줄러(순수 모듈 — 타이머·backoff·single-flight)
import {
  createSessionRefreshScheduler,
  createSingleFlight,
} from "../utils/sessionRefreshScheduler.js";
// S2 Task 1: 세션 만료 시 조용한 logout 대신 컨텍스트 유지 재인증 모달
import ReauthModal from "../components/ReauthModal.jsx";

export const AuthContext = createContext(null);
const LS_KEY = "mediaplex_auth_credential";
const LOGGED_OUT_KEY = "mediaplex_explicit_logout";

const staleAuthError = (message) => {
  const error = new Error(message);
  error.code = AUTH_OPERATION_STALE;
  return error;
};

function decodeJWT(token) {
  try {
    const payload = token.split(".")[1];
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const binary = atob(base64);
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    const decoded = new TextDecoder("utf-8").decode(bytes);
    return JSON.parse(decoded);
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sessionUnavailable, setSessionUnavailable] = useState(false);
  const [bootstrapAttempt, setBootstrapAttempt] = useState(0);
  const authRequestsRef = useRef(new Set());
  const logoutFlightRef = useRef(Promise.resolve());
  // S2 Task 1: 세션 만료 신호 (logout과 분리). 멱등 가드용 ref 동반.
  const [sessionExpired, setSessionExpired] = useState(false);
  const sessionExpiredRef = useRef(false);
  // P0 fail-closed(§5): refresh 는 이미 인증된 세션의 보호 요청 재시도 전용 — 핸들러가 mount 시 1회 wire 되는
  //  closure 라 최신 인증 상태는 ref 로 읽는다. 채택/logout 시점에 직접 동기화(effect 반영 전 호출도 정확).
  const isAuthenticatedRef = useRef(false);
  useEffect(() => {
    isAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated]);
  // Session tranche A(§A-3) principal 결속: 지금 서버가 검증해 준 principal(normalized email).
  //  재인증·선제 갱신이 받아 온 credential 은 이 값과 같은 계정일 때만 채택한다 — "서버 검증 성공"만으로는
  //  A 로 로그인한 탭이 B 계정으로 바뀌는 것을 못 막는다. logout 이 비우고, 채택 시점에만 갱신한다.
  const principalRef = useRef("");
  const appSessionExpiryRef = useRef(0);
  // §A-2·A-6 + 독립 감사(missing-global-single-flight): 갱신은 앱 전체에서 하나뿐인 진행 promise 다.
  //  timer·visibility/focus/online resume·apiClient 401 이 모두 여기에 합류한다(GIS prompt 중복 0).
  const refreshFlightRef = useRef(null);
  if (refreshFlightRef.current === null)
    refreshFlightRef.current = createSingleFlight();
  // P0 fail-closed(§6): 비동기 인증 작업 세대 가드 — 새 인증 작업 시작·명시적 logout·provider unmount 가 이전 세대를
  //  무효화한다. 모든 await 뒤 storage/user·auth state/session-expired/Sentry 를 쓰기 직전에 isCurrent 를 재확인 →
  //  stale 완료는 세션을 되살리지 못하고 고정 code/오류로 끝난다(StrictMode 1차 effect 의 완료도 unmount 로 무효화).
  const authOpRef = useRef(null);
  if (authOpRef.current === null) authOpRef.current = createAuthOpGuard();
  // Session tranche A(§A-1·A-2·A-6): 선제 갱신 스케줄러 핸들. 아래 effect 가 생성/해제한다.
  const schedulerRef = useRef(null);
  useEffect(() => {
    const guard = authOpRef.current;
    guard.mount();
    return () => guard.unmount();
  }, []);

  // P0 fail-closed(§3~§5): 검증된 credential 채택 단일 지점 — 호출부가 isCurrent 를 확인한 뒤에만 호출한다.
  //  채택 user 는 서버 응답의 user(로컬 JWT payload 아님). isAuthenticatedRef 즉시 동기화, 만료 신호 해제,
  //  Sentry identity(PROD) 는 검증된 서버 user 기준. setter/ref 만 참조 → 안정 참조(useCallback []).
  const adoptVerifiedCredential = useCallback((credential, serverUser, sessionExpiresAt = 0) => {
    if (credential) localStorage.setItem(LS_KEY, credential);
    else localStorage.removeItem(LS_KEY);
    localStorage.removeItem(LOGGED_OUT_KEY);
    appSessionExpiryRef.current = sessionExpiresAt > Date.now() ? sessionExpiresAt : 0;
    setAppSessionActive(appSessionExpiryRef.current > 0);
    // §A-3: 이후 재발급의 결속 대상 principal — 서버가 검증한 user 기준(로컬 payload 아님)
    principalRef.current = normalizeEmail(serverUser.email);
    setSessionPrincipal(principalRef.current);
    setReportDraftPrincipal(principalRef.current);
    setUser(serverUser);
    setIsAuthenticated(true);
    isAuthenticatedRef.current = true;
    sessionExpiredRef.current = false;
    setSessionExpired(false);
    if (import.meta.env.PROD) {
      Sentry.setUser({
        email: serverUser.email,
        username: serverUser.name || serverUser.email,
      });
    }
    // §A-1: 채택한 credential 의 exp 기준으로 선제 갱신을 재예약(이전 예약은 스케줄러가 교체)
    schedulerRef.current?.schedule();
  }, []);

  const verifyCandidate = useCallback((credential, options) => {
    const pending = verifyCredentialWithServer(credential, options);
    authRequestsRef.current.add(pending);
    pending.finally(() => authRequestsRef.current.delete(pending));
    return pending;
  }, []);

  // 초기화: localStorage에서 credential 복원
  useEffect(() => {
    setLoading(true);
    setSessionUnavailable(false);
    // Phase 2 Task 1: DEV bypass — Playwright 시각 확인용 mock 로그인.
    // import.meta.env.DEV 조건으로 PROD 빌드에서 Vite가 자동 dead code elimination → 프로덕션 영향 0.
    if (
      import.meta.env.DEV &&
      import.meta.env.VITE_DEV_BYPASS_AUTH === "true"
    ) {
      const mockUser = {
        email: import.meta.env.VITE_DEV_BYPASS_EMAIL || "dev@test.com",
        name: "Dev User",
        picture: "",
        email_verified: true,
      };
      setReportDraftPrincipal(mockUser.email);
      setUser(mockUser);
      setIsAuthenticated(true);
      // 같은 commit 의 DEV 훅(?mpxExpire=1 → expireSession)이 인증된 세션을 보도록 ref 도 즉시 동기화
      isAuthenticatedRef.current = true;
      setLoading(false);
      return;
    }
    if (localStorage.getItem(LOGGED_OUT_KEY)) { setLoading(false); return; }
    const sessionGuard = authOpRef.current;
    const sessionGeneration = sessionGuard.begin();
    const sessionController = new AbortController();
    let legacyCleanup;
    const restoreLegacy = () => {
    const stored = localStorage.getItem(LS_KEY);
    if (!stored) {
      setLoading(false);
      return;
    }
    const payload = decodeJWT(stored);
    if (!payload) {
      localStorage.removeItem(LS_KEY);
      setLoading(false);
      return;
    }
    // exp 체크 (초 단위)
    if (payload.exp && payload.exp * 1000 < Date.now()) {
      localStorage.removeItem(LS_KEY);
      // S2 Task 1: 만료된 세션이 있었음을 표시 → LoginPage가 조용함 대신 안내 노출
      sessionExpiredRef.current = true;
      setSessionExpired(true);
      setLoading(false);
      return;
    }
    // hd 체크
    if (payload.hd !== "opinno.co.kr") {
      localStorage.removeItem(LS_KEY);
      setLoading(false);
      return;
    }
    if (!normalizeEmail(payload.email)) {
      localStorage.removeItem(LS_KEY);
      setLoading(false);
      return;
    }
    // P0 fail-closed(docs/p0-auth-fail-closed-spec.md §3): 로컬 shape 가 valid 해도 서명 검증이 없으므로 인증
    //  상태로 채택하지 않는다. bounded /api/auth-verify 성공 + principal 일치 시에만 서버 user 를 채택하고, 그 외
    //  (네트워크·timeout·non-2xx·malformed·거부·principal 결손/불일치)는 credential 제거 + logged-out.
    //  검증 완료 전에는 loading=true 유지 → App 게이트/BrandProvider 데이터 로드가 시작되지 않는다.
    //  pathname 은 건드리지 않는다(딥링크 보존은 App 게이트 몫).
    const guard = authOpRef.current;
    const gen = guard.begin();
    const controller = new AbortController();
    verifyCandidate(stored, {
      expectedEmail: payload.email,
      signal: controller.signal,
    }).then((result) => {
      // §6: unmount/cleanup(StrictMode 1차 effect 의 abort 포함)·더 최신 인증 작업 뒤의 완료는 state 도 storage 도
      //  건드리지 않는다
      if (!guard.isCurrent(gen)) return;
      if (!result.ok) {
        localStorage.removeItem(LS_KEY);
        setLoading(false);
        return;
      }
      adoptVerifiedCredential(stored, result.user, result.sessionExpiresAt);
      setLoading(false);
    });
    return () => {
      controller.abort();
    };
    };
    readAppSession({ signal: sessionController.signal }).then(session => {
      if (!sessionGuard.isCurrent(sessionGeneration)) return;
      if (session.unavailable) { setSessionUnavailable(true); setLoading(false); return; }
      if (session.ok && session.active) {
        const stored = localStorage.getItem(LS_KEY);
        const token = isSamePrincipal(session.user.email, decodeJWT(stored)?.email) ? stored : "";
        adoptVerifiedCredential(token, session.user, session.expiresAt);
        setLoading(false);
      } else { legacyCleanup = restoreLegacy(); }
    });
    return () => { sessionController.abort(); legacyCleanup?.(); };

  }, [adoptVerifiedCredential, verifyCandidate, bootstrapAttempt]);

  // P0 fail-closed(§4): GSI credential 은 bounded /api/auth-verify 성공 + principal 일치 후에만 채택한다.
  //  네트워크 예외·timeout·non-2xx·malformed·success:false·principal 결손/불일치는 { success:false, error:고정 code }
  //  로 끝나며 storage/user/Sentry 를 쓰지 않는다. 클라이언트 측 payload 폴백은 없다(dev 브라우저 테스트는
  //  auth-verify 를 mock 한다 — 별도 폴백 env 신설 금지).
  const login = async (credential) => {
    const guard = authOpRef.current;
    const gen = guard.begin();
    await logoutFlightRef.current;
    if (!guard.isCurrent(gen)) return { success: false, error: AUTH_FAIL.UNAVAILABLE };
    const payload = decodeJWT(credential);
    const result = await verifyCandidate(credential, {
      expectedEmail: payload?.email,
    });
    // §6: 그 사이 logout·더 최신 인증 작업·unmount 가 있었으면 stale — 검증이 성공했어도 채택하지 않는다(고정 code)
    if (!guard.isCurrent(gen))
      return { success: false, error: AUTH_FAIL.UNAVAILABLE };
    if (!result.ok) return { success: false, error: result.code };
    adoptVerifiedCredential(credential, result.user, result.sessionExpiresAt);
    return { success: true };
  };

  const logout = (options) => {
    // §6: 진행 중인 login/reauth/refresh 완료를 무효화 — 늦은 성공이 세션을 되살리지 못한다
    authOpRef.current.invalidate();
    // §A-6: 예약된 선제 갱신 timer 와 진행 중 갱신의 완료도 함께 무효화
    schedulerRef.current?.stop();
    // 공유 갱신 promise 해제 + 진행 중 GIS 재발급 요청 취소(늦게 도착한 credential 이 로그아웃한 세션을
    //  되살리지 못하게 provider 프롬프트까지 닫는다)
    refreshFlightRef.current.reset();
    cancelPendingCredentialRequests();
    principalRef.current = "";
    setSessionPrincipal("");
    setReportDraftPrincipal("");
    appSessionExpiryRef.current = 0;
    setAppSessionActive(false);
    localStorage.setItem(LOGGED_OUT_KEY, "1");
    // Persist the explicit logout locally even if the browser goes offline during revocation.
    if (!options?.localOnly) {
      logoutFlightRef.current = Promise.allSettled([...authRequestsRef.current]).then(() =>
        fetch("/api/session", { method: "DELETE", credentials: "same-origin", keepalive: true, signal: AbortSignal.timeout(8000) })
      ).catch(() => {});
    }
    isAuthenticatedRef.current = false;
    localStorage.removeItem(LS_KEY);
    setUser(null);
    setIsAuthenticated(false);
    // S2 Task 1: 명시적 로그아웃은 만료 신호도 정리 (재인증 모달/안내 잔존 방지)
    sessionExpiredRef.current = false;
    setSessionExpired(false);
    // Sentry user context 제거
    if (import.meta.env.PROD) {
      Sentry.setUser(null);
    }
  };

  // S2 Task 1: 세션 만료 표시 (logout과 분리 — teardown 없이 앱 마운트 유지).
  //  apiClient 401·refresh 실패 시 logout 대신 이걸 호출 → 재인증 모달.
  //  멱등 가드: 이미 만료 표시면 무시 (동시 401 다발에도 1회만 트리거).
  const expireSession = useCallback(() => {
    if (sessionExpiredRef.current) return;
    // §6: 세션이 없으면(logout 뒤 도착한 stale 401·refresh 실패) 만료 상태를 쓰지 않는다 — 만료는 인증된 세션에만 있다
    if (!isAuthenticatedRef.current) return;
    sessionExpiredRef.current = true;
    setSessionExpired(true);
  }, []);

  // S2 Task 1: 사용자 상호작용 재인증 (모달 「다시 로그인」). 검증 강도는 login/refresh와 동일.
  //  성공 → 만료 신호 해제(직전 탭·브랜드 그대로 재개). 실패 → throw (모달이 '다시 시도' 유지).
  //  P0 fail-closed(§5·§6): 서버 검증은 bounded helper(200 + success:true + principal 일치 + timeout) 경유, 채택 user 는
  //  서버 응답. 실패/stale 은 고정 오류만 throw(원문 0) — 모달은 고정 문구를 보여준다.
  const reauthenticate = useCallback(async (credential) => {
    const guard = authOpRef.current;
    // §A-3 principal 결속: 재발급 후보는 "지금 로그인된 principal" 에 묶인다. 세션이 없으면(빈 principal)
    //  묶일 대상이 없으므로 재발급 자체를 시작하지 않는다(fail-closed). 대조는 채택 직전까지 두 번 —
    //  ① 재발급 payload ② 서버가 돌려준 user. 불일치는 p0 §51 allowlist 안의 고정 code 로만 끝나고
    //  다른 계정의 credential·user·Sentry identity 를 어디에도 남기지 않는다(화면은 그대로 유지).
    const expectedPrincipal = principalRef.current;
    if (!expectedPrincipal) throw new Error("INVALID_REAUTH_TOKEN");
    const gen = guard.begin();
    let fresh;
    try {
      fresh = credential || await requestFreshCredential({
        google: typeof window !== "undefined" ? window.google : null,
        clientId: import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID,
      });
    } catch {
      if (!guard.isCurrent(gen)) throw staleAuthError("REAUTH_SUPERSEDED");
      // p0-auth-fail-closed-spec.md §51: reauth outward error 는 고정 4종만이다. GSI 무상호작용 실패를
      //  별도 코드로 분리하는 spec §B-4 는 그 allowlist 를 넓히는 변경 — 의철 결정 전까지 기존 코드 유지.
      //  Google 원문은 싣지 않는다(§A-7).
      throw new Error("REAUTH_VERIFY_FAILED");
    }
    if (!guard.isCurrent(gen)) throw staleAuthError("REAUTH_SUPERSEDED");
    const p = decodeJWT(fresh);
    if (!p || p.hd !== "opinno.co.kr") throw new Error("INVALID_REAUTH_TOKEN");
    // ① 다른 계정의 credential 은 서버로 보내지도 않는다(계정 전환 차단 · 타 계정 자격 전송 0)
    if (!isSamePrincipal(expectedPrincipal, p.email))
      throw new Error("INVALID_REAUTH_TOKEN");
    if (p.exp && p.exp * 1000 < Date.now())
      throw new Error("REAUTH_TOKEN_EXPIRED");
    const result = await verifyCandidate(fresh, {
      expectedEmail: p.email,
    });
    if (!guard.isCurrent(gen)) throw staleAuthError("REAUTH_SUPERSEDED");
    if (!result.ok) throw new Error("REAUTH_VERIFY_FAILED");
    // ② 서버가 돌려준 user 도 현재 principal 과 일치할 때만 채택(대기 중 세션이 바뀐 경우 포함)
    if (
      !isSamePrincipal(expectedPrincipal, result.user.email) ||
      principalRef.current !== expectedPrincipal
    )
      throw new Error("INVALID_REAUTH_TOKEN");
    adoptVerifiedCredential(fresh, result.user, result.sessionExpiresAt);
    return { success: true };
  }, [adoptVerifiedCredential, verifyCandidate]);

  // Phase 2 Task 2 + Track B: silent refresh
  //  - exp 아직 유효 → success (서버 401은 transient 가능성 → 1회 재시도)
  //  - exp 만료 → GIS prompt(auto_select) 무상호작용 재발급 시도
  //    성공: 기존과 동일 검증(hd/exp + 서버 auth-verify) 후 채택 — 매 proxy 요청의
  //          tokeninfo 3중 체크(auth-verify.js)는 그대로 적용 (검증 강도 무변경)
  //    실패: throw → apiClient가 logout → 로그인 유도 (기존 동작)
  //  P0 fail-closed(§5): 미만료 credential 반환은 이미 인증된 세션의 동일 보호 요청 1회 재시도 전용 —
  //    auth state 를 만들지 않으며, 로그아웃 상태(초기 복원·login 전)에서는 어느 경로도 열지 않는다.
  //  Session tranche A: 실제 재발급(GIS prompt)은 refreshFlightRef 하나로 묶는다 — 선제 timer·resume·401 이
  //    각자 prompt 를 띄우지 않는다. 미만료 재사용(401 재시도) 경로는 prompt 가 없어 합류 대상이 아니다.
  const refreshToken = useCallback(async (opts) => {
    if (!isAuthenticatedRef.current) throw new Error("NOT_AUTHENTICATED");
    if (appSessionExpiryRef.current) {
      return refreshFlightRef.current.run(async () => {
        const guard = authOpRef.current;
        const generation = guard.begin();
        const expected = principalRef.current;
        const session = await readAppSession();
        if (!guard.isCurrent(generation) || !isAuthenticatedRef.current || principalRef.current !== expected)
          throw staleAuthError("REFRESH_SUPERSEDED");
        if (session.ok && session.active && isSamePrincipal(expected, session.user.email)) {
          appSessionExpiryRef.current = session.expiresAt;
          return localStorage.getItem(LS_KEY) || "";
        }
        if (session.unavailable) {
          const error = new Error("SESSION_TEMPORARILY_UNAVAILABLE");
          error.code = "AUTH_SERVICE_UNAVAILABLE";
          throw error;
        }
        appSessionExpiryRef.current = 0;
        setAppSessionActive(false);
        throw new Error("SESSION_EXPIRED");
      });
    }
    const credential = localStorage.getItem(LS_KEY);
    if (!credential) throw new Error("NO_CREDENTIAL");
    const payload = decodeJWT(credential);
    if (!payload || !payload.exp) throw new Error("INVALID_TOKEN");
    // §A-1: 선제 갱신(force)만 재사용 경로를 건너뛴다 — 새 credential 을 받아야 갱신이다.
    //  401 재시도 경로는 건드리지 않는다: p0-auth-fail-closed-spec.md §52(미만료 credential 반환 =
    //  인증된 세션의 동일 보호 요청 1회 retry 전용)가 승인된 계약이다. spec §A-5(거절된 토큰 비재사용)는
    //  그 계약과 충돌 — 의철 결정 전까지 미구현(session-navigation-ux-spec.md §보류 참조).
    if (payload.exp * 1000 >= Date.now() && !opts?.force) {
      // 아직 유효 — apiClient가 원 요청 재시도
      return credential;
    }
    // Track B: 만료·선제 갱신 — GIS 무상호작용 재발급 (Google 세션 쿠키 유효 시 상호작용 0).
    //  §A-3 principal 결속: 재발급 후보는 현재 검증된 principal 에 묶인다(세션 없으면 시작조차 하지 않는다).
    const expectedPrincipal = principalRef.current;
    if (!expectedPrincipal) throw new Error("INVALID_REFRESHED_TOKEN");
    // 전역 single-flight: 이미 진행 중인 갱신이 있으면 그 결과를 공유한다(GIS prompt 중복 0)
    return refreshFlightRef.current.run(async () => {
      //  §6: 세대 캡처 — logout·더 최신 인증 작업·unmount 가 있으면 완료는 stale(채택 0, 고정 오류)
      const guard = authOpRef.current;
      const gen = guard.begin();
      let fresh;
      try {
        fresh = await requestFreshCredential({
          google: typeof window !== "undefined" ? window.google : null,
          clientId: import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID,
        });
      } catch {
        if (!guard.isCurrent(gen)) throw staleAuthError("REFRESH_SUPERSEDED");
        throw new Error("REFRESH_VERIFY_FAILED");
      }
      if (!guard.isCurrent(gen)) throw staleAuthError("REFRESH_SUPERSEDED");
      const p2 = decodeJWT(fresh);
      if (!p2 || p2.hd !== "opinno.co.kr")
        throw new Error("INVALID_REFRESHED_TOKEN");
      // ① 다른 계정의 credential 은 서버로 보내지도 않는다(조용한 계정 전환 차단)
      if (!isSamePrincipal(expectedPrincipal, p2.email))
        throw new Error("INVALID_REFRESHED_TOKEN");
      if (p2.exp && p2.exp * 1000 < Date.now()) throw new Error("TOKEN_EXPIRED");
      // /api/auth-verify 서버 검증 — login/reauth 와 동일한 bounded helper(200 + success:true + principal 일치 +
      //  timeout). 실패 시 채택하지 않음. 채택 user 는 서버 응답(로컬 payload 아님).
      const result = await verifyCandidate(fresh, {
        expectedEmail: p2.email,
      });
      if (!guard.isCurrent(gen)) throw staleAuthError("REFRESH_SUPERSEDED");
      if (!result.ok) throw new Error("REFRESH_VERIFY_FAILED");
      // ② 서버가 돌려준 user 도 현재 principal 과 일치할 때만 채택
      if (
        !isSamePrincipal(expectedPrincipal, result.user.email) ||
        principalRef.current !== expectedPrincipal
      )
        throw new Error("INVALID_REFRESHED_TOKEN");
      adoptVerifiedCredential(fresh, result.user, result.sessionExpiresAt);
      return fresh;
    });
  }, [adoptVerifiedCredential, verifyCandidate]);

  // S3 §A-1·A-2·A-4·A-6: 선제 세션 갱신 배선.
  //  - exp-5분 단일 timer + visibility/focus/online resume 을 스케줄러가 single-flight 로 묶는다.
  //  - 갱신은 force — 미만료 credential 을 그대로 돌려받고 끝나지 않는다(그건 401 재시도 전용 경로).
  //  - 토큰이 유효한 동안의 실패는 앱을 막지 않고 bounded backoff(스케줄러 내부). 실제 만료 후 실패만
  //    expireSession → 재인증 모달. GSI 가 무상호작용 credential 을 못 주는 것도 여기서는 그냥 실패다.
  //  - cleanup(unmount)·명시적 logout 은 timer 와 진행 중 갱신의 완료를 무효화한다.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const scheduler = createSessionRefreshScheduler({
      getExpiryMs: () => {
        if (appSessionExpiryRef.current) return appSessionExpiryRef.current;
        const p = decodeJWT(localStorage.getItem(LS_KEY) || "");
        return p?.exp ? p.exp * 1000 : null;
      },
      refresh: () => refreshToken({ force: true }),
      onExpired: expireSession,
      isActive: () => document.visibilityState !== "hidden" && navigator.onLine !== false &&
        isAuthenticatedRef.current && !sessionExpiredRef.current,
    });
    schedulerRef.current = scheduler;
    if (isAuthenticatedRef.current) scheduler.schedule();
    const onResume = () => {
      if (document.visibilityState === "hidden") return;
      if (!isAuthenticatedRef.current || sessionExpiredRef.current) return;
      scheduler.resume();
    };
    window.addEventListener("focus", onResume);
    window.addEventListener("online", onResume);
    document.addEventListener("visibilitychange", onResume);
    return () => {
      window.removeEventListener("focus", onResume);
      window.removeEventListener("online", onResume);
      document.removeEventListener("visibilitychange", onResume);
      scheduler.stop();
      if (schedulerRef.current === scheduler) schedulerRef.current = null;
    };
  }, [refreshToken, expireSession]);

  // A different tab may already have renewed the same account. Verify before adopting it.
  useEffect(() => {
    const controllers = new Set();
    const onStorage = async (event) => {
      if (event.key !== LS_KEY || !isAuthenticatedRef.current) return;
      if (!event.newValue) { logout({ localOnly: true }); return; }
      const expected = principalRef.current;
      const payload = decodeJWT(event.newValue);
      if (!isSamePrincipal(expected, payload?.email)) { expireSession(); return; }
      if (!payload?.exp || payload.exp * 1000 <= Date.now()) return;
      const guard = authOpRef.current;
      const generation = guard.begin();
      const controller = new AbortController();
      controllers.add(controller);
      const result = await verifyCandidate(event.newValue, {
        expectedEmail: expected, signal: controller.signal,
      });
      controllers.delete(controller);
      if (!guard.isCurrent(generation) || principalRef.current !== expected ||
          localStorage.getItem(LS_KEY) !== event.newValue) return;
      if (result.ok) adoptVerifiedCredential(event.newValue, result.user, result.sessionExpiresAt);
    };
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("storage", onStorage);
      controllers.forEach(controller => controller.abort());
    };
  }, [adoptVerifiedCredential, expireSession]);

  // apiClient에 refresh/logout 핸들러 wire (두 핸들러 모두 안정 참조(useCallback) → provider mount 시 1회 실행)
  //  S2 Task 1: 401·refresh 실패 시 teardown(logout) 대신 expireSession → 재인증 모달.
  useEffect(() => {
    const clearRefreshHandler = setRefreshHandler(refreshToken);
    const clearLogoutHandler = setLogoutHandler(expireSession);
    return () => {
      clearRefreshHandler();
      clearLogoutHandler();
    };
  }, [refreshToken, expireSession]);

  // S2 Task 1 검증용 dev 전용 훅 — GIS 없이 만료 상태 강제 토글.
  //  import.meta.env.DEV 게이트 → PROD 빌드(dist)에서 Vite dead-code 제거(프로덕션 영향 0).
  //  프로덕션에 인증상태 위조 백도어를 절대 남기지 않음.
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    if (typeof window === "undefined") return;
    window.__mpxAuthDebug = {
      expire: () => expireSession(), // 세션 중 만료(인증 유지) → 재인증 모달
      setExpired: (v) => {
        sessionExpiredRef.current = !!v;
        setSessionExpired(!!v);
      },
      setAuthed: (v) => setIsAuthenticated(!!v),
      reset: () => {
        sessionExpiredRef.current = false;
        setSessionExpired(false);
      },
    };
    if (new URLSearchParams(window.location.search).get("mpxExpire") === "1") {
      expireSession();
    }
  }, [expireSession]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        loading,
        login,
        logout,
        sessionExpired,
        sessionUnavailable,
        retrySession: () => setBootstrapAttempt(value => value + 1),
        reauthenticate,
        isClientMode: false,
      }}
    >
      {children}
      {/* S2 Task 1: 세션 중 만료 → 앱 마운트 유지 + 차단형 재인증 모달.
          isAuthenticated 유지 케이스에만(페이지 로드 만료는 LoginPage가 안내). */}
      {sessionExpired && isAuthenticated && (
        <ReauthModal
          userEmail={user?.email}
          onReauth={reauthenticate}
          onLogout={logout}
        />
      )}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
